<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('groupTracking.viewLiveLocation') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('groupTracking.viewLiveLocation'),
            active: true
          }
        ]"
      />
    </div>
    <ts-loading-banner :loading="loading">
      <ts-card>
        <ts-card-wrapper>
          <div class="ts-flex ts-items-center ts-space-x-2">
            <div class="ts-flex ts-items-center ts-space-x-2">
              <label
                class="ts-mb-0 ts-pb-0 ts-whitespace-no-wrap ts-uppercase"
                >{{ $t('groupTracking.groupName') }}</label
              >
              <a-select
                v-model="model.id"
                :options="groupList"
                class="ts-w-64"
                showSearch
                :filterOption="
                  (input, option) =>
                    option.componentOptions.children[0].text
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                "
              ></a-select>
            </div>
            <div class="ts-flex ts-items-center ts-space-x-2">
              <label
                class="ts-mb-0 ts-pb-0 ts-whitespace-no-wrap ts-uppercase"
                >{{ $t('groupTracking.userStatus') }}</label
              >
              <a-select
                v-model="model.status"
                :options="[
                  { value: '', label: 'All' },
                  { value: 'Online', label: 'Online' },
                  { value: 'Offline', label: 'Offline' }
                ]"
                class="ts-w-48"
              ></a-select>
            </div>
            <ts-button outline color="primary" @click.prevent="onView">
              {{ $t('view') }}
            </ts-button>
            <ts-button
              color="info"
              outline
              class="ts-h-8"
              @click.prevent="onExportExcel"
            >
              <i class="fas fa-file-excel"></i>
              {{ $t('excel') }}</ts-button
            >
          </div>
        </ts-card-wrapper>
        <div class="ts-relative">
          <gmap-map
            ref="gmap"
            :center="center"
            :zoom="zoom"
            style="width:100%;  height: calc(100vh - 103px)"
          >
            <gmap-info-window
              v-for="(win, index) in markers"
              :options="{
                maxWidth: 400,
                pixelOffset: { width: 0, height: -35 }
              }"
              :position="{
                lat: win.lat,
                lng: win.long
              }"
              :opened="true"
            >
              <div>
                <b>{{ win.user_name }}</b>
                <p
                  class="ts-m-0 ts-text-white ts-px-1 ts-rounded-md"
                  :class="{
                    'ts-bg-red-500': win.status === 'Offline',
                    'ts-bg-green-500': win.status === 'Online'
                  }"
                  style="width: fit-content;"
                >
                  <small>{{ win.status }}</small>
                </p>
              </div>
            </gmap-info-window>
            <gmap-marker
              v-for="(s, index) in markers"
              :key="index"
              :position="{
                lat: s.lat,
                lng: s.long
              }"
              :icon="{
                url: icon,
                scaledSize: { width: 35, height: 35 },
                labelOrigin: {
                  x: 30,
                  y: -2
                }
              }"
            />
          </gmap-map>
          <div
            class="ts-absolute ts-top-0 ts-right-0 ts-w-56 ts-m-2 ts-mt-16 ts-bg-transparent ts-p-0"
            style="height: 68%"
          >
            <div
              class="ts-bg-gray-300 ts-rounded ts-opacity-75 ts-w-full ts-h-full ts-shadow"
              style="backdrop-filter: blur(10px)"
            ></div>

            <div
              class="ts-w-full ts-h-full ts-absolute ts-top-0 ts-left-0 ts-overflow-y-scroll"
            >
              <div
                style="border-bottom: 1px solid #cfcfcf;"
                class="ts-px-3 ts-py-2 ts-cursor-pointer hover:ts-bg-gray-200 ts-transition-all ts-duration-200"
                v-for="(u, index) in userOnMaps"
                :key="index"
              >
                <div
                  class="ts-flex ts-items-center"
                  @click.prevent="clickPaneTo(u)"
                >
                  <div class="ts-w-12 ts-h-8 ts-flex ts-items-center">
                    <i class="far fa-user fa-3x"></i>
                  </div>
                  <div>
                    <b>{{ u.user_name }}</b>
                    <p
                      class="ts-m-0 ts-text-white ts-px-1 ts-rounded-md"
                      :class="{
                        'ts-bg-red-500': u.status === 'Offline',
                        'ts-bg-green-500': u.status === 'Online'
                      }"
                      style="width: fit-content;"
                    >
                      <small>{{ u.status }}</small>
                    </p>
                    <p class="ts-m-0">
                      <small>{{ u.phone }}</small>
                    </p>
                    <p class="ts-m-0">{{ u.tracking_time }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ts-card>
    </ts-loading-banner>
  </div>
</template>

<script>
import moment from 'moment'
import { trim } from 'lodash'
import { mapActions } from 'vuex'
import * as FileDownload from 'downloadjs'
import { Errors } from 'form-backend-validation'
import icon from '@/assets/marker/people-location.svg'
export default {
  name: 'mapView',
  data() {
    return {
      icon,
      loading: false,
      center: { lng: 105.57359185298843, lat: 12.291513194851614 },
      zoom: 8,
      model: {
        id: '',
        status: ''
      },
      groupList: [{ value: '', label: 'All' }],
      userOnMaps: [],
      errors: new Errors()
    }
  },
  created() {
    this.fetchGroup()
    if (this.$route.query.id) {
      this.model.id = parseInt(this.$route.query.id, 10)
      this.onView()
    }
  },
  computed: {
    markers() {
      return this.userOnMaps.filter(u => (u.lat !== null) & (u.long !== null))
    }
  },
  methods: {
    ...mapActions('sale/groupTracking', ['exportExcel']),
    fetchGroup() {
      this.loading = true
      this.$store
        .dispatch('sale/groupTracking/getGroup')
        .then(groups => {
          groups.data.forEach(el => {
            this.groupList.push({
              value: el.id,
              label: el.group_name
            })
          })
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onView() {
      this.loading = true
      this.$store
        .dispatch('sale/groupTracking/viewOnMap', {
          filter: {
            group_id: this.model.id,
            status: this.model.status
          }
        })
        .then(response => {
          this.userOnMaps = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    clickPaneTo(record) {
      if ((record.lat !== null) & (record.long !== null)) {
        this.$refs.gmap.$mapPromise.then(map => {
          map.panTo({
            lat: record.lat,
            lng: record.long
          })
          this.zoom = 18
        })
      }
    },
    onCloseWinInfo(event) {
      console.log(event)
    },
    onExportExcel() {
      this.loading = true
      this.errors = new Errors()
      this.exportExcel({
        group_id: this.model.id,
        status: this.model.status
      })
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.loading = false))
    }
  }
}
</script>
<style>
.gm-ui-hover-effect {
  opacity: 0.6;
  display: none !important;
}
</style>
